import CryptoJS from 'crypto-js';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet, apiPost, apiPut, apiRemove } from 'api-service';
import { toast } from 'lib';
import translation from 'translation';
import { mapToApiPlatform } from 'utils';
import { contentAdapter } from 'store/content/adapters';
import {
  allContentsAdapter,
  archiveAdapter,
  autoBulletinAdapter,
  bulletinWebPlatformAdapter,
  webBulletinAdapter,
} from './autoBulletin.adapters';
import { prepareParamsForSaveAutoBulletin } from './autoBulletin.helpers';

const { t } = translation;

export const fetchAutoBulletins = createAsyncThunk(
  'autoBulletinReducer/fetchAutoBulletins',
  async (_, thunkAPI) => {
    try {
      const response = await apiGet('/bulletin/v2/smart/config');
      return (
        response.data?.result?.data?.map((bulletin) =>
          autoBulletinAdapter(bulletin)
        ) ?? []
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchAutoBulletin = createAsyncThunk(
  'autoBulletinReducer/fetchAutoBulletin',
  async (params, thunkAPI) => {
    try {
      const response = await apiGet(`/bulletin/v2/smart/config/${params?.id}`);
      return autoBulletinAdapter(response.data?.result?.data) ?? [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchAutoBulletinByHash = createAsyncThunk(
  'autoBulletinReducer/fetchAutoBulletinByHash',
  async ({ hash, bulletinId }, thunkAPI) => {
    try {
      const response = await apiGet(`/bulletin/v3/export/smart/${hash}`, {
        'with-bulletin': bulletinId,
      });
      return autoBulletinAdapter(response.data?.result?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteAutoBulletin = createAsyncThunk(
  'autoBulletinReducer/deleteAutoBulletin',
  async (params, thunkAPI) => {
    try {
      await apiRemove(`/bulletin/v2/smart/config/${params?.id}`);
      toast({
        title: t('common:successOperation'),
        description: t('bulletin:success.deleteAutoBulletin.description'),
      });
      return params?.id;
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorOperation'),
        description: t('bulletin:error.deleteAutoBulletin.description'),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const toggleActivationAutoBulletin = createAsyncThunk(
  'autoBulletinReducer/toggleActivationAutoBulletin',
  async (params, thunkAPI) => {
    try {
      await apiPost(`/bulletin/v2/smart/config/${params?.id}/toggle-active`);
      toast({
        title: t('common:successOperation'),
        description: t(
          'bulletin:success.toggleActivationAutoBulletin.description'
        ),
      });
      return {
        id: params?.id,
        singleAutoBulletin: params?.singleAutoBulletin ?? false,
      };
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorOperation'),
        description: t(
          'bulletin:error.toggleActivationAutoBulletin.description'
        ),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const shareHashAutoBulletin = createAsyncThunk(
  'autoBulletinReducer/shareHashAutoBulletin',
  async (params, thunkAPI) => {
    try {
      const response = await apiPost(
        `/bulletin/v2/smart/config/${params?.id}/share-hash`
      );
      toast({
        title: t('common:successOperation'),
        description: t('bulletin:success.shareHashAutoBulletin.description'),
      });
      return { id: params?.id, hash: response?.data?.result?.data?.hash };
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorOperation'),
        description: t('bulletin:error.shareHashAutoBulletin.description'),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addAutoBulletin = createAsyncThunk(
  'autoBulletinReducer/addAutoBulletin',
  async (params, thunkAPI) => {
    const autoBulletinState = thunkAPI.getState().autoBulletinReducer;

    const _params = prepareParamsForSaveAutoBulletin(
      autoBulletinState,
      params?.onlyDefaultParams ?? false
    );

    try {
      const response = await apiPost('/bulletin/v2/smart/config', _params);
      toast({
        title: t('common:successOperation'),
        description: t('bulletin:success.addAutoBulletin.description'),
      });
      return autoBulletinAdapter(response.data?.result?.data) ?? [];
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorOperation'),
        description:
          error?.response?.data?.errors?.data?.message ||
          t('bulletin:error.addAutoBulletin.description'),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateAutoBulletin = createAsyncThunk(
  'autoBulletinReducer/updateAutoBulletin',
  async (params, thunkAPI) => {
    const autoBulletinState = thunkAPI.getState().autoBulletinReducer;

    const _params = prepareParamsForSaveAutoBulletin(
      autoBulletinState,
      params?.onlyDefaultParams ?? false
    );

    try {
      const response = await apiPut(
        `/bulletin/v2/smart/config/${autoBulletinState.currentBulletin?.id}`,
        _params
      );
      toast({
        title: t('common:successOperation'),
        description: t('bulletin:success.updateAutoBulletin.description'),
      });
      return autoBulletinAdapter(response.data?.result?.data) ?? [];
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorOperation'),
        description: t('bulletin:error.updateAutoBulletin.description'),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchAutoBulletinArchives = createAsyncThunk(
  'autoBulletinReducer/fetchAutoBulletinArchives',
  async (hash, thunkAPI) => {
    try {
      const response = await apiGet(`/bulletin/v3/export/smart/${hash}/menu`);
      return (
        response.data?.result?.data?.map((archive) =>
          archiveAdapter(archive)
        ) || []
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchAutoBulletinContents = createAsyncThunk(
  'autoBulletinReducer/fetchAutoBulletinContents',
  async ({ hash, bulletinId, platforms }, thunkAPI) => {
    const { currentAutoBulletin } = thunkAPI.getState().autoBulletinReducer;
    const selectedPlatforms = platforms || currentAutoBulletin.platforms;

    try {
      if (!selectedPlatforms.length) {
        return {
          allContents: {},
          allPinnedContents: {},
          summary: null,
          summeryContentCount: 0,
        };
      }

      const response = await apiGet(
        `/bulletin/v3/export/smart/${hash}/${bulletinId}`,
        {
          platforms: selectedPlatforms.map(
            (platform) => mapToApiPlatform[platform]
          ),
        }
      );

      return allContentsAdapter(response);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteAutoBulletinContent = createAsyncThunk(
  'autoBulletinReducer/deleteAutoBulletinContent',
  async ({ bulletinId, platform, contentIds, undoable }, thunkAPI) => {
    try {
      await apiRemove(`/bulletin/${bulletinId}/content`, {
        platform: mapToApiPlatform[platform],
        post_ids: contentIds,
      });
      if (!undoable)
        toast({
          title: t('common:successOperation'),
          description: t('bulletin:success.removeBulletinContent', {
            count: contentIds.length,
          }),
        });
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorOperation'),
        description: t('bulletin:error.removeBulletinContent', {
          count: contentIds.length,
        }),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const pinAutoBulletinContents = createAsyncThunk(
  'autoBulletinReducer/pinAutoBulletinContents',
  async (
    { bulletinId, platform, contentIds, isUnpin, selectedContents },
    thunkAPI
  ) => {
    try {
      await apiPost(`/bulletin/${bulletinId}/sorts`, {
        platform: mapToApiPlatform[platform],
        sort_type: 'manual',
        sort_list: contentIds,
      });
      toast({
        title: t('common:successOperation'),
        description: isUnpin
          ? t('bulletin:success.unpinBulletinContent')
          : t('bulletin:success.pinBulletinContent', {
              count: selectedContents.length,
            }),
      });
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorOperation'),
        description: isUnpin
          ? t('bulletin:error.unpinBulletinContent')
          : t('bulletin:error.pinBulletinContent', {
              count: selectedContents.length,
            }),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const sendRatedFeedback = createAsyncThunk(
  'autoBulletinReducer/sendRatedFeedback',
  async (params, thunkAPI) => {
    const { type, id, rate, ...rest } = params;

    const finalParams = {
      entity_type: type,
      entity_id: id,
      rate: rate,
      ...rest,
    };

    const signature = JSON.stringify(finalParams);
    finalParams.signature = CryptoJS.SHA1(signature).toString(CryptoJS.enc.Hex);

    try {
      const response = await apiPost('/search/feedback', finalParams);
      return response.data?.result?.data;
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorOperation'),
        description:
          error.response?.data?.errors?.data?.message ||
          t('common:error.ratedFeedback.description'),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/**
 * Bulletin Web API's
 */

export const fetchWebBulletinContents = createAsyncThunk(
  'autoBulletinReducer/fetchWebBulletinContents',
  async ({ hash, perPage, page, platform }, thunkAPI) => {
    try {
      const response = await apiGet(`/bulletin/v3/bulletin/${hash}/content`, {
        platform: mapToApiPlatform[platform] || 'news',
        per_page: perPage,
        page,
      });

      return response.data?.result?.data.map((content) =>
        contentAdapter(content, platform)
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchWebBulletinByHash = createAsyncThunk(
  'autoBulletinReducer/fetchWebBulletinByHash',
  async ({ hash }, thunkAPI) => {
    try {
      const response = await apiGet(`/bulletin/v3/bulletin/${hash}/bulletin`);
      return webBulletinAdapter(response.data?.result?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchWebBulletinPlatforms = createAsyncThunk(
  'autoBulletinReducer/fetchWebBulletinPlatforms',
  async ({ hash }, thunkAPI) => {
    try {
      const response = await apiGet(
        `/bulletin/v3/bulletin/${hash}/content/counter/platform`
      );
      return bulletinWebPlatformAdapter(response.data?.result?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const uploadBulletinImage = createAsyncThunk(
  'autoBulletinReducer/uploadBulletinImage',
  async (params, thunkAPI) => {
    try {
      const response = await apiPost(
        '/bulletin/v2/upload/image',
        params?.formData
      );
      return {
        data: response.data?.result?.data,
      };
    } catch (error) {
      toast({
        type: 'error',
        title: t('common:errorInSave'),
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);
